import { Autocomplete, AutocompleteProps, TextField } from "@mui/material";
import { Optional } from "@tanstack/react-query";
import { Control, Controller, FieldValues, Path } from "react-hook-form";

interface RHFAutocompleteFieldProps<
  O extends { id: number; name: string },
  Multiple extends boolean | undefined,
  DisableClearable extends boolean | undefined,
  FreeSolo extends boolean | undefined,
  TField extends FieldValues
> {
  autocompleteProps?: Omit<
    Optional<
      AutocompleteProps<O, Multiple, DisableClearable, FreeSolo>,
      "renderInput"
    >,
    "getOptionLabel"
  > & {};
  control: Control<TField>;
  name: Path<TField>;
  options: O[];
  placeholder?: string;
  label?: string;
  onChanged?: () => void;
  onClick?: () => void;
}

type RHFAutocompleteFieldPropType<
  O extends { id: number; name: string },
  Multiple extends boolean | undefined,
  DisableClearable extends boolean | undefined,
  FreeSolo extends boolean | undefined,
  TField extends FieldValues
> = RHFAutocompleteFieldProps<O, Multiple, DisableClearable, FreeSolo, TField>;

export const AutoCompleteElement = <
  O extends { id: number; name: string },
  Multiple extends boolean | undefined,
  DisableClearable extends boolean | undefined,
  FreeSolo extends boolean | undefined,
  TField extends FieldValues = FieldValues
>({
  ...props
}: RHFAutocompleteFieldPropType<
  O,
  Multiple,
  DisableClearable,
  FreeSolo,
  TField
>): React.ReactElement => {
  return <Component {...props} />;
};

const Component = <
  O extends { id: number; name: string },
  Multiple extends boolean | undefined,
  DisableClearable extends boolean | undefined,
  FreeSolo extends boolean | undefined,
  TField extends FieldValues = FieldValues
>({
  autocompleteProps,
  ...props
}: RHFAutocompleteFieldPropType<
  O,
  Multiple,
  DisableClearable,
  FreeSolo,
  TField
>) => {
  const { control, options, name, onChanged, onClick } = props;

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => {
        const { onChange, value, ref } = field;
        return (
          <>
            <Autocomplete
              value={
                value
                  ? options.find((option) => {
                      return value == option.id;
                    }) ?? null
                  : null
              }
              getOptionLabel={(option) => {
                return option.name;
              }}
              onChange={(_: any, newValue) => {
                onChange(newValue ? newValue.id : null);

                if (onChanged !== undefined) {
                  onChanged();
                }
              }}
              options={options}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={props.label}
                  placeholder={props.placeholder}
                  inputRef={ref}
                  error={!!error}
                  helperText={error?.message}
                  InputProps={{
                    ...params.InputProps,
                  }}
                />
              )}
            />
          </>
        );
      }}
    />
  );
};
